import mustache from "mustache";
import { BaseRequest } from "../../../../base/request/BaseRequest";
import { toDateOnly } from "../../../../utils/fechaUtils";
import { FormField } from "../../../base/form/FormField";
import { IInputDateField } from "../../../definitions/form/inputs/IInputDateField";
import { DateConfig } from "../../../definitions/form/inputs/config/DateConfig";
import template from "./template.html";

mustache.parse(template);

/** Campo de fecha (`<input type="date">`) del sistema nuevo. Enlaza un string ISO
 *  del request. */
export class InputDateField<TRequest extends BaseRequest>
  extends FormField<DateConfig<TRequest>>
  implements IInputDateField
{
  private readonly _request: TRequest;

  constructor(request: TRequest, config: DateConfig<TRequest>) {
    super(config);
    this._request = request;
  }

  private _onValueChange?: () => void;

  setOnChange(handler: () => void): void {
    this._onValueChange = handler;
  }

  /** Valor (ISO) actual (desde el request). */
  getValue(): string {
    return this._config.get(this._request);
  }

  getTemplate(): string {
    return template;
  }

  getData(): unknown {
    return {
      label: this._config.label,
      description: this._config.description ?? "",
      min: this._config.min ?? null,
      max: this._config.max ?? null,
      // El <input type="date"> exige "yyyy-mm-dd"; normaliza por si viene con hora.
      value: toDateOnly(this._config.get(this._request)),
      id: this._formFieldId,
    };
  }

  connect(): void {
    this._input = document.getElementById(
      `input-${this._formFieldId}`,
    ) as HTMLInputElement;
    this._error = document.getElementById(`error-${this._formFieldId}`);
    this._input?.addEventListener("input", this._onInput);
  }

  doDispose(): void {
    this._input?.removeEventListener("input", this._onInput);
  }

  getError(): string | null {
    const value = this._input?.value ?? "";
    const { min, max } = this._config;
    if (min && value && value < min)
      return `La fecha no puede ser anterior a ${min}`;
    if (max && value && value > max)
      return `La fecha no puede ser posterior a ${max}`;
    return null;
  }

  showError(): void {
    const error = this.getError();
    if (this._error) this._error.innerHTML = error ?? "";
    this._input?.classList.toggle("error", error !== null);
  }

  focus(): void {
    this._input?.focus();
  }

  disabled(): void {
    if (this._input) this._input.disabled = true;
  }

  enabled(): void {
    if (this._input) this._input.disabled = false;
  }

  private _input: HTMLInputElement | null = null;
  private _error: HTMLElement | null = null;

  private _onInput = (): void => {
    if (!this._input) return;
    this._config.set(this._request, this._input.value);
    this.showError();
    this._onValueChange?.();
  };
}
